import { useEffect, useRef } from 'react'

type CloseOnInteractionsOptions = {
  isActive: boolean
  onClose: () => void
  enabled?: boolean
  closeOnScroll?: boolean
  shouldIgnoreEvent?: (event: PointerEvent) => boolean
}

export function useCloseOnInteractions<T extends HTMLElement>({
  isActive,
  onClose,
  enabled = true,
  closeOnScroll = false,
  shouldIgnoreEvent,
}: CloseOnInteractionsOptions) {
  const ref = useRef<T>(null)

  useEffect(() => {
    if (!enabled || !isActive) {
      return
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (shouldIgnoreEvent?.(event)) {
        return
      }

      if (!ref.current?.contains(event.target as Node)) {
        onClose()
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    const handleScroll = () => {
      onClose()
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    
    if (closeOnScroll) {
      // Use capture to catch scroll on any scrollable element
      window.addEventListener('scroll', handleScroll, true)
    }

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
      
      if (closeOnScroll) {
        window.removeEventListener('scroll', handleScroll, true)
      }
    }
  }, [enabled, isActive, onClose, closeOnScroll, shouldIgnoreEvent])

  return ref
}
